import * as React                     from 'react';
import { useTranslation }             from 'react-i18next';
import { Navigation, Thumbs }         from 'swiper/modules';
import { Swiper, SwiperSlide }        from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper/types';

import InternalVideo from '../../../../components/internal-video';

import { upper } from '../../utils';

import CompletedProjectArrowLeft  from '../arrow-left';
import CompletedProjectArrowRight from '../arrow-right';
import CompletedProjectButton     from '../button';
import { GalleryItem }            from '../item';
import CompletedProjectPicture    from '../picture';

import { CompletedProjectModalProps } from './completed-project.modal.props';
import './completed-project.modal.style.scss';

const CompletedProjectModal: React.FC<CompletedProjectModalProps> = function(props) {
  const { t } = useTranslation();

  const {
          doorHandle,
          gallery,
          id,
          inside,
          label,
          outside,
          series,
        } = props;

  const arrowLeftRef  = React.useRef<HTMLDivElement | null>(null);
  const arrowRightRef = React.useRef<HTMLDivElement | null>(null);

  const [thumbsSwiper, setThumbsSwiper] = React.useState<SwiperClass | null>(null);

  const parameters = React.useMemo(() => {
    const items: Record<string, string> = {};

    if (typeof outside === 'string' && outside.trim().length > 0) {
      items.outside_title = outside;
    }

    if (typeof inside === 'string' && inside.trim().length > 0) {
      items.inside_title = inside;
    }

    if (typeof doorHandle === 'string' && doorHandle.trim().length > 0) {
      items.door_handle_title = doorHandle;
    }

    return items;
  }, [doorHandle, inside, outside]);
  const slides     = React.useMemo(() => {
    return gallery
      .sort((a, b) => {
        return a.priority < b.priority ? -1 : 1;
      })
      .map((item) => {
        return {
          priority: item.priority,
          src:      item.src,
          type:     item.type,
        } as GalleryItem;
      });
  }, [gallery]);

  return (
    <div className={'completed-project-modal'}>
      <div className={'gallery'}>
        <Swiper
          modules={[Navigation, Thumbs]}
          navigation={{
            nextEl: arrowRightRef.current,
            prevEl: arrowLeftRef.current,
          }}
          observer
          rewind
          slidesPerView={1}
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
          watchSlidesProgress
        >
          {slides.map((slide) => {
            return (
              <SwiperSlide key={slide.priority}>
                {slide.type?.startsWith('image')
                  ? (
                    <CompletedProjectPicture
                      src={slide.src}
                      type={slide.type}
                    />
                  )
                  : slide.type?.startsWith('video')
                    ? <InternalVideo src={slide.src} />
                    : null
                }
              </SwiperSlide>
            );
          })}
        </Swiper>
        <div className={'thumbs'}>
          <CompletedProjectArrowLeft ref={arrowLeftRef} />
          <Swiper
            centeredSlidesBounds
            centerInsufficientSlides
            modules={[Thumbs]}
            normalizeSlideIndex
            onSwiper={(swiper) => {
              setThumbsSwiper(swiper);
            }}
            breakpoints={{
              470: {
                slidesPerView: 2,
              },
              590: {
                slidesPerView: 3,
              },
              710: {
                slidesPerView: 4,
              },
              830: {
                slidesPerView: 5,
              },
            }}
            slidesPerView={1}
            spaceBetween={4}
          >
            {slides.map((slide) => {
              return (
                <SwiperSlide
                  key={slide.priority}
                >
                  {slide.type?.startsWith('image')
                    ? (
                      <CompletedProjectPicture
                        src={slide.src}
                        type={slide.type}
                      />
                    )
                    : slide.type?.startsWith('video')
                      ? (
                        <InternalVideo
                          src={slide.src}
                          thumbnail
                        />
                      )
                      : null
                  }
                </SwiperSlide>
              );
            })}
          </Swiper>
          <CompletedProjectArrowRight ref={arrowRightRef} />
        </div>
      </div>
      <div className={'content'}>
        <div className={'label'}>{label}</div>
        {Object.keys(parameters).length > 0 && (
          <ul className={'parameters'}>
            {Object
              .entries(parameters)
              .map(([key, value]) => {
                return <li key={key}>{upper(t(key))}: {value}</li>;
              })
            }
          </ul>
        )}
        <div className={'series'}>{upper(t('series_label'))} {series}</div>
        <CompletedProjectButton id={id} />
      </div>
    </div>
  );
};

export default CompletedProjectModal;